import PropTypes from "prop-types";

import { Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";

import { formatDate, formatMoney } from "@/features/shared";
import { UpstreamTransactionLink } from "@/features/ui";
import { ReturnableTypeEnum, ReturnableTypeLabel } from "../../returnable-type";

export const GeneralInformationSection = ({ salesReturn }) => {
  // Check if this is a Non-Faktur return (empty SI with SI-SRT prefix)
  const isNonInvoiceReturn =
    salesReturn.returnableType === ReturnableTypeEnum.SALES_INVOICE &&
    salesReturn.returnable?.referenceNumber?.startsWith("SI-SRT");

  const displayReturnableType = isNonInvoiceReturn
    ? ReturnableTypeEnum.NON_INVOICE
    : salesReturn.returnableType;

  const showPricing =
    salesReturn.returnableType === ReturnableTypeEnum.SALES_INVOICE ||
    salesReturn.returnableType === ReturnableTypeEnum.NON_INVOICE;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1">Informasi Umum</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Dibuat oleh
          </Typography>
          <Typography variant="body1">
            {salesReturn.creator?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Tanggal Transaksi
          </Typography>
          <Typography variant="body1">
            {formatDate(salesReturn.transactionDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pelanggan
          </Typography>
          <Typography variant="body1">
            {salesReturn.buyer?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Outlet
          </Typography>
          <Typography variant="body1">
            {salesReturn.seller?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Jenis Retur
          </Typography>
          <Typography variant="body1">
            {displayReturnableType
              ? ReturnableTypeLabel[displayReturnableType]
              : "Non Faktur"}
          </Typography>
        </Grid>
        {!isNonInvoiceReturn && salesReturn.returnableType && (
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Return Dari
            </Typography>
            <UpstreamTransactionLink
              referenceNumber={salesReturn.returnable?.referenceNumber}
              shortReference={false}
            />
          </Grid>
        )}
        {showPricing && (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Subtotal
              </Typography>
              <Typography variant="body1">
                {formatMoney(salesReturn.subtotal)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Pajak
              </Typography>
              <Typography variant="body1">
                {formatMoney(salesReturn.taxAmount)} (
                {salesReturn.taxPercentage}%)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Diskon
              </Typography>
              <Typography variant="body1">
                {salesReturn.discountType === "PERCENTAGE"
                  ? `${salesReturn.discountAmount ?? 0}% (~${formatMoney(
                      ((salesReturn.subtotal ?? 0) *
                        (salesReturn.discountAmount ?? 0)) /
                        100,
                    )})`
                  : formatMoney(salesReturn.discountAmount ?? 0)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Total Diskon
              </Typography>
              <Typography variant="body1">
                {formatMoney(
                  salesReturn.totalDiscount ?? salesReturn.discount ?? 0,
                )}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Grand Total
              </Typography>
              <Typography variant="body1" sx={{
                fontWeight: "bold"
              }}>
                {formatMoney(salesReturn.grandTotal)}
              </Typography>
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Catatan
          </Typography>
          <Typography variant="body1">{salesReturn.note ?? "-"}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  salesReturn: PropTypes.object.isRequired,
};
